import { ContextProvider } from '@/app/providers/ContextProvider';
import { store } from '@/app/store/store';
import { isActive } from '@/features/chats/sub-features/audio-recorder/utils/recorderSessions';
import { Username } from '@/features/users';
import { CHAT_CONSTANTS } from '@/shared/constants/chatConstants';
import useBehavioralKeyboard from '@/shared/hooks/useBehavioralKeyboard';
import { getLocalUserId, getUserIdFromJid } from '@/shared/utils';
import storage from '@/shared/utils/storage';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { Provider } from 'react-redux';
import { useDraft } from '../model/draft';
import { useGroupParticipants } from '../model/groupParticipants';
import { useMediaFile } from '../sub-features/media/viewmodel/useMediaFile';
import { useTypingStatus } from '../sub-features/typing-indicator/viewmodel/useTypingStatus';
import { useChatFeature } from '../chatsRegistry';
import { useEditMessage } from './useEditMessage';

// ─── Constants ────────────────────────────────────────────────────────────────

const EMPTY_PATTERNS = new Set(['', '<br>', '<div><br></div>', '<div></div>']);
export const checkIsEmpty = content => !content || EMPTY_PATTERNS.has(content.trim());

// ─── Chip factory (module-level, never recreated) ─────────────────────────────

const createChip = ({ userId }) => {
   const componentHtml = renderToString(
      <Provider store={store}>
         <ContextProvider>
            <Username
               userId={userId}
               prefix={CHAT_CONSTANTS.CHIP_PREFIX}
               isLocal={userId === storage.getItem('username')}
               contentEditable="false"
               suppressContentEditableWarning
            />
         </ContextProvider>
      </Provider>,
   );

   return `<span class="${CHAT_CONSTANTS.MENTION_CHIP_CLASS}" contenteditable="false" data-user-id="${userId}" userId="${userId}">${componentHtml}</span>`;
};

// ─── Selection helpers (pure, no closure needed) ──────────────────────────────

const getActiveRange = () => {
   const sel = window.getSelection();
   return sel?.rangeCount > 0 ? sel.getRangeAt(0) : null;
};

const setCaretAfter = node => {
   const range = document.createRange();
   range.setStartAfter(node);
   range.collapse(true);
   const sel = window.getSelection();
   sel.removeAllRanges();
   sel.addRange(range);
   return range;
};

const placeCaretAtEnd = (el, shouldFocus = true) => {
   if (!el) return;
   const range = document.createRange();
   range.selectNodeContents(el);
   range.collapse(false);
   const sel = window.getSelection();
   sel.removeAllRanges();
   sel.addRange(range);
   if (shouldFocus) el.focus();
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useChatInput = ({ activeChatJid, fileId = null, disableDraft = true, onSend, isAttachment = true }) => {
   const activeChatId = getUserIdFromJid(activeChatJid);

   // Feature gates
   const isTypingEnabled = useChatFeature('typing-indicator');
   const isAudioEnabled = useChatFeature('audio-recorder');
   const isMentionEnabled = useChatFeature('mention');
   const { editMessageId } = useEditMessage({ activeChatJid });
   const { media: isAttachmentEnabledFeature } = useChatFeature('attachments') || {};
   const isAttachmentEnabled = isAttachmentEnabledFeature && !editMessageId && isAttachment;

   // State
   const [showMention, setShowMention] = useState(false);
   const [mentionSearch, setMentionSearch] = useState('');
   const [selectedIndex, setSelectedIndex] = useState(0);
   const [hasText, setHasText] = useState(false);
   const [isMultiLine, setIsMultiLine] = useState(false);
   // Seed from per-chat sessions map so each chat independently shows its recorder
   const [isRecording, setIsRecording] = useState(() => isActive(activeChatJid));

   // Refs
   const inputRef = useRef(null);
   const lastRangeRef = useRef(null);

   // Sub-hooks
   const { sendTypingStatus, sendTypingGoneStatus } = useTypingStatus(isTypingEnabled ? activeChatJid : null);
   const { draft, saveDraft, clearReplyTo } = useDraft({ activeChatId, fileId });
   const { caption, saveCaption, openMediaPreview } = useMediaFile({
      activeChatId,
      fileId: fileId || draft.activeFileId,
   });
   const { participants: rawParticipants } = useGroupParticipants(activeChatId, mentionSearch);
   const currentUserId = getLocalUserId();
   const filteredParticipants = useMemo(
      () => rawParticipants.filter(p => p.userId !== currentUserId),
      [rawParticipants, currentUserId],
   );

   // ── Inline helpers (no useCallback — deps would make them equally heavy) ──────

   const resetMention = () => {
      setShowMention(false);
      setMentionSearch('');
      setSelectedIndex(0);
   };

   // ── Seed input when chat or file changes ─────────────────────────────────────
   useEffect(() => {
      if (!inputRef.current) return;
      const html = fileId ? (caption?.caption ?? '') : (draft.message ?? '');
      inputRef.current.innerHTML = html;
      setHasText(!checkIsEmpty(html));

      setIsMultiLine(inputRef.current.scrollHeight > 30);
      resetMention(); // Close any open mention window when switching context

      // Only force focus if we are on initial mount or if the input was already focused.
      // This prevents "stealing" focus when swiping through media in the preview.
      const isAlreadyFocused = document.activeElement === inputRef.current;
      const isInitialMount = !lastRangeRef.current; // Simple heuristic for first seed
      placeCaretAtEnd(inputRef.current, isAlreadyFocused || isInitialMount);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [activeChatId, fileId, editMessageId]);

   const syncInput = useCallback(
      content => {
         setHasText(!checkIsEmpty(content));
         if (inputRef.current) {
            setIsMultiLine(inputRef.current.scrollHeight > 30);
         }

         if (disableDraft) {
            if (fileId) saveCaption(content);
            else saveDraft(content);
         }
      },
      [disableDraft, fileId, saveCaption, saveDraft],
   );

   // Not memoized — no deps, plain function reference is stable enough for a ref-read helper
   const saveSelection = () => {
      const range = getActiveRange();
      if (range && inputRef.current?.contains(range.commonAncestorContainer)) {
         lastRangeRef.current = range.cloneRange();
      }
   };

   // ── Send ──────────────────────────────────────────────────────────────────────
   const sendMessage = useCallback(
      ({ isMedia = false } = {}) => {
         const message = inputRef.current?.innerHTML ?? '';
         if (!checkIsEmpty(message) || fileId || isMedia) {
            if (isTypingEnabled) sendTypingGoneStatus();
            onSend?.(message);
            clearReplyTo();
            if (inputRef.current) inputRef.current.innerHTML = '';
            setHasText(false);
            setIsMultiLine(false);
         }
      },
      [clearReplyTo, fileId, isTypingEnabled, onSend, sendTypingGoneStatus],
   );

   // ── Mention insert ────────────────────────────────────────────────────────────
   const insertMention = useCallback(
      user => {
         if (!inputRef.current) return;
         inputRef.current.focus();
         const range = getActiveRange();
         const textNode = range?.startContainer;
         if (!range || textNode.nodeType !== Node.TEXT_NODE) return;

         const atIndex = textNode.textContent.lastIndexOf(CHAT_CONSTANTS.MENTION_TRIGGER, range.endOffset - 1);
         if (atIndex === -1) return;

         range.setStart(textNode, atIndex);
         range.setEnd(textNode, range.endOffset);
         range.deleteContents();

         const chipHtml = createChip(user);
         const fragment = range.createContextualFragment(chipHtml);
         const chipNode = fragment.firstChild;
         const space = document.createTextNode(CHAT_CONSTANTS.NON_BREAKING_SPACE);

         range.insertNode(fragment);
         range.setStartAfter(chipNode);
         range.insertNode(space);
         setCaretAfter(space);

         resetMention();
         syncInput(inputRef.current.innerHTML);
      },

      [syncInput],
   );

   // ── Event handlers ────────────────────────────────────────────────────────────
   const handleInput = useCallback(() => {
      const content = inputRef.current.innerHTML;
      syncInput(content);
      if (!checkIsEmpty(content) && isTypingEnabled) sendTypingStatus();

      if (!isMentionEnabled) {
         resetMention();
         return;
      }

      const sel = window.getSelection();
      const range = sel?.rangeCount > 0 ? sel.getRangeAt(0) : null;
      const pre = range?.cloneRange();
      if (pre) {
         pre.selectNodeContents(inputRef.current);
         pre.setEnd(range.endContainer, range.endOffset);
      }
      const text = pre?.toString() ?? '';
      const lastAtIndex = text.lastIndexOf(CHAT_CONSTANTS.MENTION_TRIGGER);

      if (lastAtIndex !== -1) {
         const after = text.slice(lastAtIndex + 1);
         if (!after.includes(CHAT_CONSTANTS.NEWLINE) && !after.startsWith(' ') && !after.startsWith('\u00A0')) {
            setShowMention(true);
            setMentionSearch(after);
            return;
         }
      }
      resetMention();
   }, [isMentionEnabled, isTypingEnabled, sendTypingStatus, syncInput]);

   // Behavioral Keyboard (Standardized Logic)
   const { handleKeyDown } = useBehavioralKeyboard(inputRef, {
      edgeAware: isAttachmentEnabled || fileId,
      onEnter: () => {
         if (showMention && filteredParticipants.length > 0) {
            insertMention(filteredParticipants[selectedIndex]);
            return;
         }
         sendMessage();
      },
      onEscape: () => (showMention ? resetMention() : false),
      onArrowUp: () => {
         if (showMention && filteredParticipants.length > 0) {
            setSelectedIndex(p => (p - 1 + filteredParticipants.length) % filteredParticipants.length);
            return true;
         }
         return false;
      },
      onArrowDown: () => {
         if (showMention && filteredParticipants.length > 0) {
            setSelectedIndex(p => (p + 1) % filteredParticipants.length);
            return true;
         }
         return false;
      },
   });

   const handleEmoji = useCallback(
      emoji => {
         inputRef.current?.focus();
         const sel = window.getSelection();
         let range = lastRangeRef.current;

         if (range) {
            sel.removeAllRanges();
            sel.addRange(range);
         } else {
            range = sel?.rangeCount > 0 ? sel.getRangeAt(0) : document.createRange();
            if (!sel?.rangeCount) {
               range.selectNodeContents(inputRef.current);
               range.collapse(false);
               sel.removeAllRanges();
               sel.addRange(range);
            }
         }

         range.deleteContents();
         const node = document.createTextNode(emoji);
         range.insertNode(node);
         lastRangeRef.current = setCaretAfter(node).cloneRange();
         handleInput();
      },
      [handleInput],
   );

   const handleAudioComplete = useCallback(
      async audioFile => {
         if (audioFile) {
            await openMediaPreview(audioFile, 'audio', false);
            sendMessage({ isMedia: true });
         }
         setIsRecording(false);
      },
      [openMediaPreview, sendMessage],
   );

   // ── handleUserSelect: kept for external UI chips that pass names ──────────────
   const handleUserSelect = useCallback(
      userName => {
         const user = filteredParticipants.find(
            u => (u.userProfile?.name || u.userProfile?.nickName || u.userId) === userName,
         );
         if (user) insertMention(user);
      },
      [filteredParticipants, insertMention],
   );

   // ── Paste: strip rich HTML to plain text, re-insert at caret ─────────────────
   const handlePaste = useCallback(
      e => {
         e.preventDefault();

         const text = e.clipboardData?.getData('text/plain') ?? '';
         if (!text) return;

         const sel = window.getSelection();
         let range = lastRangeRef.current;
         if (range) {
            sel.removeAllRanges();
            sel.addRange(range);
         } else if (sel.rangeCount > 0) {
            range = sel.getRangeAt(0);
         } else {
            placeCaretAtEnd(inputRef.current);
            range = getActiveRange();
         }

         if (!range) return;

         range.deleteContents();

         // ── Split on newlines and insert <br> between lines ──────────────────────
         const lines = text.split('\n');
         const fragment = document.createDocumentFragment();
         let lastNode = null;

         lines.forEach((line, i) => {
            // Skip leading/trailing empty lines but preserve internal ones
            const textNode = document.createTextNode(line);
            fragment.appendChild(textNode);
            lastNode = textNode;

            if (i < lines.length - 1) {
               const br = document.createElement('br');
               fragment.appendChild(br);
               lastNode = br;
            }
         });

         range.insertNode(fragment);

         // Place caret after the last inserted node
         lastRangeRef.current = setCaretAfter(lastNode).cloneRange();

         syncInput(inputRef.current.innerHTML);
         if (isTypingEnabled) sendTypingStatus();
      },
      [isTypingEnabled, sendTypingStatus, syncInput],
   );

   return {
      isAudioEnabled,
      isMentionEnabled,
      isAttachmentEnabled,
      inputRef,
      hasText,
      isMultiLine,
      showMention,
      isRecording,
      setIsRecording,
      selectedIndex,
      filteredParticipants,
      activeChatId,
      sendMessage,
      handleInput,
      handleKeyDown,
      saveSelection,
      handleEmoji,
      handleUserSelect,
      handleAudioComplete,
      handlePaste,
   };
};
